import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { faBarcode, faClose, faReceipt, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ModalPortal from "../../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../../components/Animate/Animate"
import defaultStyle from "../../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../../components/Titles/Titles"
import { ButtonIcon } from "../../../../components/Button/Button"
import InputField from "../../../../components/InputField/InputField"
import { GenericDropDown } from "../../../../components/Dropdown/Dropdown"
import THEME from "../../../../configuration/Theme"
import { SIZE } from "../../../../configuration/Sizes"
import { EFiscalTaxRegime } from "../../../../Objects/FiscalIntegration/EFiscalTaxRegime"
import { type IFiscalProductProfile } from "../../../../Objects/FiscalIntegration/IFiscalProductProfile"
import {
  type FiscalProductProfileField,
  type FiscalProductProfileValidationErrors,
  validateFiscalProductProfile,
} from "../../../../Objects/FiscalIntegration/validateFiscalProductProfile"
import { PopupHelpButton } from "../../../../components/Help/PopupHelpButton"

type Props = {
  isOpen: boolean
  profile: IFiscalProductProfile | null
  isBulk: boolean
  taxRegime: EFiscalTaxRegime
  onChange: (profile: IFiscalProductProfile) => void
  onClose: () => void
  onSave: () => void
}

export const FiscalProductProfilePopup: React.FC<Props> = ({
  isOpen,
  profile,
  isBulk,
  taxRegime,
  onChange,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation(["fiscalIntegration", "buttons", "helpPopup"])

  const [validationVisible, setValidationVisible] = useState(false)

  useEffect(() => {
    setValidationVisible(false)
  }, [isOpen, isBulk, profile?.genericFoodEntityId, taxRegime])

  const fieldErrors = useMemo<FiscalProductProfileValidationErrors>(
    () => (validationVisible && profile ? validateFiscalProductProfile(profile, taxRegime) : {}),
    [profile, taxRegime, validationVisible],
  )

  if (!isOpen || !profile) {
    return null
  }

  const patch = (next: Partial<IFiscalProductProfile>) => onChange({ ...profile, ...next })

  const fieldId = (field: FiscalProductProfileField) => `fiscal-product-${field}`

  const getError = (field: FiscalProductProfileField) => {
    const messageKey = fieldErrors[field]

    return messageKey ? t(messageKey) : undefined
  }

  const usesIcmsCst = taxRegime === EFiscalTaxRegime.SimpleNationalExcess || taxRegime === EFiscalTaxRegime.Normal

  const requiresTaxReform = taxRegime === EFiscalTaxRegime.Normal

  const number = (value: string) => {
    const parsed = Number(value)

    return Number.isFinite(parsed) ? parsed : 0
  }

  const handleSave = () => {
    const errors = validateFiscalProductProfile(profile, taxRegime)

    const firstInvalidField = Object.keys(errors)[0] as FiscalProductProfileField | undefined

    if (firstInvalidField) {
      setValidationVisible(true)
      window.requestAnimationFrame(() => document.getElementById(fieldId(firstInvalidField))?.focus())
      return
    }

    setValidationVisible(false)

    onSave()
  }

  const errorCount = Object.keys(fieldErrors).length

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              icon={faReceipt}
              iconColor={THEME.colors.iconBGColor}
              title={isBulk ? t("products.bulkTitle") : t("products.editTitle", { name: profile.productName })}
            >
              <div className={`${defaultStyle.ButtonsActions} gap-1`}>
                <PopupHelpButton
                  variant="global"
                  helpKey="pages.myrestaurant.fiscalintegration.productprofile.1"
                  title={t("helpPopup:global.fiscalProductProfile.title")}
                  description={t("helpPopup:global.fiscalProductProfile.description")}
                  steps={t("helpPopup:global.fiscalProductProfile.steps", { returnObjects: true }) as string[]}
                  bullets={t("helpPopup:global.fiscalProductProfile.bullets", { returnObjects: true }) as string[]}
                />

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.button.onClose"
                />
              </div>
            </TitlePageResponsiveIconSmall>

            <div className="max-h-[76vh] overflow-y-auto p-2">
              <section className="mb-3 rounded-xl border-2 border-amber-100 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
                <strong className="block text-sm">{t("products.accountantTitle")}</strong>

                <p className="mt-1">{t("products.accountantDescription")}</p>
              </section>

              {validationVisible && errorCount > 0 && (
                <section
                  className="mb-3 flex gap-3 rounded-xl border-2 border-red-300 bg-red-50 p-3 text-red-900"
                  role="alert"
                  aria-live="assertive"
                >
                  <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 shrink-0" />
                  <div>
                    <strong className="block text-sm">{t("validation.summary", { count: errorCount })}</strong>
                    <p className="mt-1 text-xs">{t("validation.summaryDescription")}</p>
                  </div>
                </section>
              )}

              <div className="p-3 space-y-4">
                <GenericDropDown
                  id={fieldId("origin")}
                  legend={t("fields.origin")}
                  initialState={String(profile.origin)}
                  data={Array.from({ length: 9 }, (_, value) => ({
                    value: String(value),
                    label: t(`origin.${value}`),
                  }))}
                  errorMessage={getError("origin")}
                  handlerFuncion={(event) => patch({ origin: Number(event.target.value) })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.select.origin"
                />

                <InputField
                  id={fieldId("productCode")}
                  type="text"
                  icon={faBarcode}
                  value={profile.productCode}
                  placeholder={t("fields.productCode")}
                  maxLength={60}
                  errorMessage={getError("productCode")}
                  onChange={(event) => patch({ productCode: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.productCode"
                />

                <InputField
                  id={fieldId("ncm")}
                  type="text"
                  value={profile.ncm}
                  placeholder={t("fields.ncm")}
                  maxLength={8}
                  required
                  inputMode="numeric"
                  errorMessage={getError("ncm")}
                  onChange={(event) => patch({ ncm: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.ncm"
                />

                <InputField
                  id={fieldId("cest")}
                  type="text"
                  value={profile.cest ?? ""}
                  placeholder={t("fields.cest")}
                  maxLength={7}
                  inputMode="numeric"
                  errorMessage={getError("cest")}
                  onChange={(event) => patch({ cest: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.cest"
                />

                <InputField
                  id={fieldId("cfop")}
                  type="text"
                  value={profile.cfop}
                  placeholder={t("fields.cfop")}
                  maxLength={4}
                  required
                  inputMode="numeric"
                  errorMessage={getError("cfop")}
                  onChange={(event) => patch({ cfop: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.cfop"
                />

                <InputField
                  id={fieldId("unit")}
                  type="text"
                  value={profile.unit}
                  placeholder={t("fields.unit")}
                  maxLength={6}
                  required
                  errorMessage={getError("unit")}
                  onChange={(event) => patch({ unit: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.unit"
                />

                {usesIcmsCst ? (
                  <InputField
                    id={fieldId("icmsCst")}
                    type="text"
                    value={profile.icmsCst ?? ""}
                    placeholder={t("fields.icmsCst")}
                    maxLength={3}
                    required
                    inputMode="numeric"
                    errorMessage={getError("icmsCst")}
                    onChange={(event) => patch({ icmsCst: event.target.value })}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.icmsCst"
                  />
                ) : (
                  <InputField
                    id={fieldId("csosn")}
                    type="text"
                    value={profile.csosn ?? ""}
                    placeholder={t("fields.csosn")}
                    maxLength={3}
                    required
                    inputMode="numeric"
                    errorMessage={getError("csosn")}
                    onChange={(event) => patch({ csosn: event.target.value })}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.csosn"
                  />
                )}

                <InputField
                  id={fieldId("icmsRate")}
                  type="number"
                  value={String(profile.icmsRate)}
                  placeholder={t("fields.icmsRate")}
                  min={0}
                  max={100}
                  step="0.01"
                  errorMessage={getError("icmsRate")}
                  onChange={(event) => patch({ icmsRate: number(event.target.value) })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.icmsRate"
                />

                <InputField
                  id={fieldId("pisCst")}
                  type="text"
                  value={profile.pisCst}
                  placeholder={t("fields.pisCst")}
                  maxLength={2}
                  required
                  inputMode="numeric"
                  errorMessage={getError("pisCst")}
                  onChange={(event) => patch({ pisCst: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.pisCst"
                />

                <InputField
                  id={fieldId("pisRate")}
                  type="number"
                  value={String(profile.pisRate)}
                  placeholder={t("fields.pisRate")}
                  min={0}
                  max={100}
                  step="0.01"
                  errorMessage={getError("pisRate")}
                  onChange={(event) => patch({ pisRate: number(event.target.value) })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.pisRate"
                />

                <InputField
                  id={fieldId("cofinsCst")}
                  type="text"
                  value={profile.cofinsCst}
                  placeholder={t("fields.cofinsCst")}
                  maxLength={2}
                  required
                  inputMode="numeric"
                  errorMessage={getError("cofinsCst")}
                  onChange={(event) => patch({ cofinsCst: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.cofinsCst"
                />

                <InputField
                  id={fieldId("cofinsRate")}
                  type="number"
                  value={String(profile.cofinsRate)}
                  placeholder={t("fields.cofinsRate")}
                  min={0}
                  max={100}
                  step="0.01"
                  errorMessage={getError("cofinsRate")}
                  onChange={(event) => patch({ cofinsRate: number(event.target.value) })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.cofinsRate"
                />

                <InputField
                  id={fieldId("taxBenefitCode")}
                  type="text"
                  value={profile.taxBenefitCode ?? ""}
                  placeholder={t("fields.taxBenefitCode")}
                  maxLength={10}
                  errorMessage={getError("taxBenefitCode")}
                  onChange={(event) => patch({ taxBenefitCode: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.taxBenefitCode"
                />

                <InputField
                  id={fieldId("ibsCbsCst")}
                  type="text"
                  value={profile.ibsCbsCst ?? ""}
                  placeholder={t("fields.ibsCbsCst")}
                  maxLength={3}
                  required={requiresTaxReform}
                  inputMode="numeric"
                  errorMessage={getError("ibsCbsCst")}
                  onChange={(event) => patch({ ibsCbsCst: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.ibsCbsCst"
                />

                <InputField
                  id={fieldId("ibsCbsClassificationCode")}
                  type="text"
                  value={profile.ibsCbsClassificationCode ?? ""}
                  placeholder={t("fields.ibsCbsClassification")}
                  maxLength={6}
                  required={requiresTaxReform}
                  inputMode="numeric"
                  errorMessage={getError("ibsCbsClassificationCode")}
                  onChange={(event) => patch({ ibsCbsClassificationCode: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.ibsCbsClassification"
                />

                <InputField
                  id={fieldId("ibsStateRate")}
                  type="number"
                  value={String(profile.ibsStateRate)}
                  placeholder={t("fields.ibsStateRate")}
                  min={0}
                  max={100}
                  step="0.01"
                  errorMessage={getError("ibsStateRate")}
                  onChange={(event) => patch({ ibsStateRate: number(event.target.value) })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.ibsStateRate"
                />

                <InputField
                  id={fieldId("ibsMunicipalRate")}
                  type="number"
                  value={String(profile.ibsMunicipalRate)}
                  placeholder={t("fields.ibsMunicipalRate")}
                  min={0}
                  max={100}
                  step="0.01"
                  errorMessage={getError("ibsMunicipalRate")}
                  onChange={(event) => patch({ ibsMunicipalRate: number(event.target.value) })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.ibsMunicipalRate"
                />

                <InputField
                  id={fieldId("cbsRate")}
                  type="number"
                  value={String(profile.cbsRate)}
                  placeholder={t("fields.cbsRate")}
                  min={0}
                  max={100}
                  step="0.01"
                  errorMessage={getError("cbsRate")}
                  onChange={(event) => patch({ cbsRate: number(event.target.value) })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.input.cbsRate"
                />
              </div>

              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={handleSave}
                  className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700"
                  data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalProductProfilePopup.button.handleSave"
                >
                  {t("buttons:button_save")}
                </button>
              </div>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
